import React, { MouseEvent } from 'react';
import { IconButton, ListItem, styled, Typography } from '@mui/material';
import { MoreHoriz } from '@mui/icons-material';
import { Surgerie } from 'types/surgerie';

type SurgerieItemModuleProps = {
  surgerie: Surgerie;
  onActionClick(event: MouseEvent<HTMLButtonElement>, surgerie: Surgerie): void;
};

const ListItemStyled = styled(ListItem)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'stretch',
  gap: theme.spacing(1.5),
  padding: theme.spacing(1.5),
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius,
  backgroundColor: theme.palette.background.paper,
}));

const Header = styled('div')(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'minmax(0, 1fr) auto',
  gap: theme.spacing(1),
  alignItems: 'flex-start',
}));

const Title = styled('div')({
  minWidth: 0,
});

const HeaderActions = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  gap: theme.spacing(0.25),
  whiteSpace: 'nowrap',
}));

const Status = styled('span')(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  width: 'fit-content',
  maxWidth: '100%',
  padding: '4px 8px',
  borderRadius: theme.shape.borderRadius,
  backgroundColor: theme.palette.grey[200],
  color: theme.palette.text.primary,
  fontSize: 12,
  fontWeight: 700,
  lineHeight: 1.2,
  '&.open': {
    backgroundColor: theme.palette.success.main,
    color: theme.palette.success.contrastText,
  },
  '&.closed': {
    backgroundColor: theme.palette.info.main,
    color: theme.palette.info.contrastText,
  },
}));

const Content = styled('div')(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: theme.spacing(1.25),
  [theme.breakpoints.down('sm')]: {
    gridTemplateColumns: '1fr',
  },
}));

const Values = styled('div')(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
  gap: theme.spacing(0.75),
  paddingTop: theme.spacing(1.25),
  borderTop: `1px solid ${theme.palette.divider}`,
  [theme.breakpoints.down('md')]: {
    gridTemplateColumns: '1fr 1fr',
  },
}));

const ValueField = styled('div')(({ theme }) => ({
  minWidth: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(0.25),
  padding: theme.spacing(1),
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius,
  backgroundColor: theme.palette.grey[50],
  '&.primary': {
    borderColor: theme.palette.primary.light,
    backgroundColor: theme.palette.primary.main,
    '& p, & span': {
      color: theme.palette.primary.contrastText,
    },
  },
  '&.success': {
    borderColor: theme.palette.success.light,
    backgroundColor: theme.palette.success.main,
    '& p, & span': {
      color: theme.palette.success.contrastText,
    },
  },
}));

const Field = styled('div')({
  minWidth: 0,
});

const Description = styled(Typography)({
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  display: '-webkit-box',
  WebkitLineClamp: 1,
  lineClamp: 1,
  WebkitBoxOrient: 'vertical',
});

const ValueLabel = styled(Typography)({
  lineHeight: 1.2,
});

const Value = styled(Typography)({
  textAlign: 'right',
  lineHeight: 1.25,
  whiteSpace: 'nowrap',
});

const SurgerieItemModule: React.FC<SurgerieItemModuleProps> = ({ surgerie, onActionClick }) => {
  return (
    <ListItemStyled>
      <Header>
        <Title>
          <Description variant="subtitle2" fontWeight={700}>
            #{surgerie.id_int} - {surgerie.formattedProcedureName}
          </Description>
          <Typography variant="caption" color="textSecondary">
            Criado em {surgerie.formattedCreatedAt}
          </Typography>
        </Title>
        <HeaderActions>
          <Status className={surgerie.status}>{surgerie.formattedStatus}</Status>
          <IconButton size="small" color="default" onClick={event => onActionClick(event, surgerie)}>
            <MoreHoriz fontSize="small" />
          </IconButton>
        </HeaderActions>
      </Header>

      <Content>
        <Field>
          <Typography variant="caption" color="textSecondary">
            Paciente
          </Typography>
          <Description variant="body2">{surgerie.formattedPatientName}</Description>
        </Field>
        <Field>
          <Typography variant="caption" color="textSecondary">
            Médico
          </Typography>
          <Description variant="body2">{surgerie.formattedDoctorName}</Description>
        </Field>
        <Field>
          <Typography variant="caption" color="textSecondary">
            Hospital
          </Typography>
          <Description variant="body2">{surgerie.formattedHospitalName}</Description>
        </Field>
        <Field>
          <Typography variant="caption" color="textSecondary">
            Procedimento
          </Typography>
          <Description variant="body2">{surgerie.formattedProcedureName}</Description>
        </Field>
      </Content>

      <Values>
        <ValueField>
          <ValueLabel variant="caption" color="textSecondary">
            Médico
          </ValueLabel>
          <Value variant="body2" fontWeight={600}>
            {surgerie.formattedDoctorValue}
          </Value>
        </ValueField>
        <ValueField>
          <ValueLabel variant="caption" color="textSecondary">
            Hospital
          </ValueLabel>
          <Value variant="body2" fontWeight={600}>
            {surgerie.formattedHospitalValue}
          </Value>
        </ValueField>
        <ValueField>
          <ValueLabel variant="caption" color="textSecondary">
            Pacotes
          </ValueLabel>
          <Value variant="body2" fontWeight={600}>
            {surgerie.formattedPackagesValue}
          </Value>
        </ValueField>
        <ValueField>
          <ValueLabel variant="caption" color="textSecondary">
            Anestesista
          </ValueLabel>
          <Value variant="body2" fontWeight={600}>
            {surgerie.formattedAnesthetistValue}
          </Value>
        </ValueField>
        <ValueField>
          <ValueLabel variant="caption" color="textSecondary">
            Markup
          </ValueLabel>
          <Value variant="body2" fontWeight={600}>
            {surgerie.formattedMarkupValue}
          </Value>
        </ValueField>

        {surgerie.status === 'closed' && (
          <>
            <ValueField>
              <ValueLabel variant="caption" color="textSecondary">
                Entrada
              </ValueLabel>
              <Value variant="body2" fontWeight={600}>
                {surgerie.formattedValueEntry}
              </Value>
            </ValueField>
            <ValueField className="success">
              <ValueLabel variant="caption">Pago</ValueLabel>
              <Value variant="body1" fontWeight={800}>
                {surgerie.formattedValuePaid}
              </Value>
            </ValueField>
          </>
        )}
        <ValueField className="primary">
          <ValueLabel variant="caption">Total</ValueLabel>
          <Value variant="body1" fontWeight={800}>
            {surgerie.formattedTotalValue}
          </Value>
        </ValueField>
      </Values>
    </ListItemStyled>
  );
};

export default SurgerieItemModule;
